import React, { useEffect, useMemo, useState } from 'react';
import {
  Card, Button, Table, Tag, Statistic, Row, Col, Typography, Alert, Popconfirm, Modal, Form,
  Input, InputNumber, Segmented, Space, Tooltip, Empty, theme,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  ApiOutlined, PlusOutlined, EditOutlined, DeleteOutlined, SaveOutlined,
  SettingOutlined, ExperimentOutlined, WarningOutlined,
} from '@ant-design/icons';
import {
  calcMotor, calcRollup, DEFAULT_PF_SETTINGS, PFMotorInput, PFMotorResult, PFSettings, PFMethod,
} from './powerFactorCalc';
import {
  getPFMotors, savePFMotor, deletePFMotor, getPFSettings, savePFSettings, PFMotor,
} from '../../firebase/powerFactor';
import { PFWaveform, WaveLegend, WaveformExplorer } from './PFWaveform';
import { phaseAngle, waveStats } from './waveforms';

interface Props { projectId: string; }

const f1 = (v: number) => v.toFixed(1);
const f2 = (v: number) => v.toFixed(2);
const money = (v: number) => `$${Math.round(v).toLocaleString()}`;

const METHOD_TAG: Record<PFMethod, { color: string; label: string }> = {
  capacitor: { color: 'green', label: 'Capacitor' },
  vfd: { color: 'blue', label: 'VFD candidate' },
  none: { color: 'default', label: 'No correction' },
};

/* The 2026-10-21 walkthrough motor — pre-fills the form so the tool can be demoed quickly. */
const EXAMPLE_MOTOR: PFMotorInput = {
  tag: 'EX-1', location: 'Example (meeting walkthrough)', voltage: 480, phase: 3, amps: 20,
  measuredPf: 0.68, hoursPerDay: 24, daysPerYear: 365,
};

const NEW_MOTOR: Partial<PFMotorInput> = { voltage: 480, phase: 3, hoursPerDay: 24, daysPerYear: 365 };

// ─── Power triangle (before solid, after dashed) ───────────────────────────

const PowerTriangle: React.FC<{ kw: number; kvarBefore: number; kvarAfter: number }> = ({ kw, kvarBefore, kvarAfter }) => {
  const { token } = theme.useToken();
  if (kw <= 0) return null;
  const W = 260, H = 170, pad = 28;
  const scale = Math.min((W - pad * 2) / kw, (H - pad * 2) / Math.max(kvarBefore, 0.001));
  const x0 = pad, y0 = pad, x1 = pad + kw * scale;
  const yB = y0 + kvarBefore * scale, yA = y0 + kvarAfter * scale;
  return (
    <svg viewBox={`0 0 ${W} ${H}`} width="100%" style={{ maxWidth: 320 }} role="img"
      aria-label="Power triangle before and after correction">
      <line x1={x0} y1={y0} x2={x1} y2={y0} stroke={token.colorText} strokeWidth={2} />
      <line x1={x1} y1={y0} x2={x1} y2={yB} stroke={token.colorError} strokeWidth={2} />
      <line x1={x0} y1={y0} x2={x1} y2={yB} stroke={token.colorTextSecondary} strokeWidth={2} />
      <line x1={x0} y1={y0} x2={x1} y2={yA} stroke={token.colorPrimary} strokeWidth={2} strokeDasharray="5 4" />
      <text x={(x0 + x1) / 2} y={y0 - 8} textAnchor="middle" fontSize={11} fill={token.colorText}>kW {f1(kw)}</text>
      <text x={x1 + 4} y={(y0 + yB) / 2} fontSize={11} fill={token.colorError}>kVAR {f1(kvarBefore)}</text>
      <text x={x1 + 4} y={yA + 12} fontSize={11} fill={token.colorPrimary}>{f1(kvarAfter)}</text>
      <text x={x0} y={H - 6} fontSize={10} fill={token.colorTextSecondary}>solid = as-found · dashed = corrected</text>
    </svg>
  );
};

// ─── Module ────────────────────────────────────────────────────────────────

const PowerFactorModule: React.FC<Props> = ({ projectId }) => {
  const { token } = theme.useToken();
  const [motors, setMotors] = useState<PFMotor[]>([]);
  const [settings, setSettings] = useState<PFSettings>(DEFAULT_PF_SETTINGS);
  const [settingsDirty, setSettingsDirty] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState<{ id?: string; values: Partial<PFMotorInput> } | null>(null);
  const [form] = Form.useForm<PFMotorInput>();
  const watched = Form.useWatch([], form) as Partial<PFMotorInput> | undefined;

  useEffect(() => {
    Promise.all([getPFMotors(projectId, setMotors), getPFSettings(projectId, setSettings)])
      .then(([m, s]) => { setMotors(m); setSettings(s); })
      .catch(() => setError('Failed to load power factor data'))
      .finally(() => setLoading(false));
  }, [projectId]);

  const results = useMemo(
    () => new Map(motors.map(m => [m.id, calcMotor(m, settings)])), [motors, settings]);
  const rollup = useMemo(() => calcRollup(motors, settings), [motors, settings]);

  const preview: PFMotorResult | null = useMemo(() => {
    if (!watched?.voltage || !watched?.amps || !watched?.measuredPf) return null;
    return calcMotor({ ...NEW_MOTOR, ...watched } as PFMotorInput, settings);
  }, [watched, settings]);

  const openEditor = (values: Partial<PFMotorInput>, id?: string) => {
    setEditing({ id, values });
    form.resetFields();
    form.setFieldsValue(values as PFMotorInput);
  };

  const handleSaveMotor = async () => {
    const values = await form.validateFields();
    setSaving(true); setError('');
    try {
      const id = await savePFMotor(projectId, values, editing?.id);
      const now = new Date().toISOString();
      setMotors(prev => editing?.id
        ? prev.map(m => (m.id === id ? { ...m, ...values, updatedAt: now } : m))
        : [...prev, { ...values, id, createdAt: now, updatedAt: now }]);
      setEditing(null);
    } catch (err: any) { setError(`Failed to save motor: ${err?.message ?? err?.code ?? 'Unknown error'}`); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id: string) => {
    try { await deletePFMotor(projectId, id); setMotors(prev => prev.filter(m => m.id !== id)); }
    catch { setError('Failed to delete motor'); }
  };

  const updateSetting = <K extends keyof PFSettings>(key: K, value: PFSettings[K] | null) => {
    if (value === null) return;
    setSettings(s => ({ ...s, [key]: value }));
    setSettingsDirty(true);
  };

  const handleSaveSettings = async () => {
    setError('');
    try { await savePFSettings(projectId, settings); setSettingsDirty(false); }
    catch (err: any) { setError(`Failed to save settings: ${err?.message ?? err?.code ?? 'Unknown error'}`); }
  };

  const columns: ColumnsType<PFMotor> = [
    {
      title: 'Motor', dataIndex: 'tag',
      render: (_, m) => (
        <div>
          <Typography.Text strong>{m.tag}</Typography.Text>
          {m.location && <div><Typography.Text type="secondary" style={{ fontSize: 12 }}>{m.location}</Typography.Text></div>}
        </div>
      ),
    },
    { title: 'HP', dataIndex: 'hp', align: 'right', render: v => v ?? '—' },
    { title: 'Supply', key: 'supply', render: (_, m) => `${m.voltage} V ${m.phase}φ` },
    { title: 'Amps', dataIndex: 'amps', align: 'right' },
    {
      title: 'PF', key: 'pf', align: 'right',
      render: (_, m) => {
        const r = results.get(m.id)!;
        return r.valid ? <span>{f2(m.measuredPf)} → <b>{f2(r.pfAfter)}</b></span> : '—';
      },
    },
    { title: 'kW', key: 'kw', align: 'right', render: (_, m) => f1(results.get(m.id)!.kw) },
    { title: 'kVAR needed', key: 'kvar', align: 'right', render: (_, m) => f2(results.get(m.id)!.kvarNeeded) },
    {
      title: 'Capacitor', key: 'cap', align: 'right',
      render: (_, m) => {
        const c = results.get(m.id)!.capacitorKvar;
        return c === null ? <Tag color="orange">Bank</Tag> : c === 0 ? '—' : <b>{c} kVAR</b>;
      },
    },
    { title: 'Amps after', key: 'ampsAfter', align: 'right', render: (_, m) => f1(results.get(m.id)!.ampsAfter) },
    {
      title: 'Method', key: 'method',
      render: (_, m) => {
        const r = results.get(m.id)!;
        const t = METHOD_TAG[r.method];
        return (
          <Space size={4}>
            <Tag color={t.color} style={{ marginInlineEnd: 0 }}>{t.label}</Tag>
            {r.warnings.length > 0 && (
              <Tooltip title={r.warnings.join(' ')}><WarningOutlined style={{ color: token.colorWarning }} /></Tooltip>
            )}
          </Space>
        );
      },
    },
    {
      title: '', key: 'actions', width: 80, align: 'right',
      render: (_, m) => (
        <Space size={0}>
          <Button type="text" size="small" icon={<EditOutlined />} aria-label="Edit motor"
            onClick={() => { const { id, createdAt, updatedAt, ...rest } = m; openEditor(rest, id); }} />
          <Popconfirm title="Delete this motor?" onConfirm={() => handleDelete(m.id)} okText="Delete" okButtonProps={{ danger: true }}>
            <Button type="text" size="small" danger icon={<DeleteOutlined />} aria-label="Delete motor" />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const detail = (m: PFMotor) => {
    const r = results.get(m.id)!;
    const item = (label: string, value: React.ReactNode) => (
      <Col xs={12} md={6}><Statistic title={label} value={value as any} valueStyle={{ fontSize: 16 }} /></Col>
    );
    return (
      <div>
        <Row gutter={[16, 12]}>
          {item('kVA before → after', `${f1(r.kvaBefore)} → ${f1(r.kvaAfter)}`)}
          {item('kVAR before → after', `${f1(r.kvarBefore)} → ${f1(r.kvarAfter)}`)}
          {item(`µF/phase (wye${m.phase === 3 ? ' · delta' : ''})`,
            m.phase === 3 ? `${f1(r.microfaradsWye)} · ${f1(r.microfaradsDelta)}` : f1(r.microfaradsWye))}
          {item('Upstream loss reduction', `${f1(r.lossReductionPct)}%`)}
          {item('Annual energy', `${Math.round(r.annualKwh).toLocaleString()} kWh`)}
          {item('Energy savings / yr', money(r.energySavingsPerYear))}
          {item('Installed cost', m.installedCost ? money(m.installedCost) : '—')}
          {item('Payback', r.paybackYears ? `${f1(r.paybackYears)} yr` : '—')}
        </Row>
        {r.valid && (() => {
          const before = phaseAngle(m.measuredPf, 'lagging');
          const after = phaseAngle(r.pfAfter, r.leading ? 'leading' : 'lagging');
          const pct = (phi: number) => `${waveStats(phi).returnedEnergyPct.toFixed(1)}% returned`;
          return (
            <div style={{ marginTop: 12 }}>
              <Row gutter={16}>
                <Col xs={24} md={12}>
                  <PFWaveform phi={before} height={130} compact label={`As-found · PF ${f2(m.measuredPf)} lag · ${pct(before)}`} />
                </Col>
                <Col xs={24} md={12}>
                  <PFWaveform phi={after} height={130} compact
                    label={`Corrected · PF ${f2(r.pfAfter)} ${r.leading ? 'lead' : 'lag'} · ${pct(after)}`} />
                </Col>
              </Row>
              <div style={{ marginTop: 4 }}><WaveLegend /></div>
            </div>
          );
        })()}
        {r.warnings.map(w => <Alert key={w} type="warning" showIcon message={w} style={{ marginTop: 8 }} />)}
        {m.notes && <Typography.Paragraph type="secondary" style={{ marginTop: 8, marginBottom: 0 }}>{m.notes}</Typography.Paragraph>}
      </div>
    );
  };

  const settingField = (label: string, key: keyof PFSettings, props: Record<string, any>) => (
    <div>
      <div style={{ fontSize: 13, marginBottom: 6 }}>{label}</div>
      <InputNumber style={{ width: '100%' }} value={settings[key] as number}
        onChange={v => updateSetting(key, v as number | null)} {...props} />
    </div>
  );

  const totalSavings = rollup.energySavingsPerYear + rollup.demandSavingsPerYear;

  return (
    <div>
      <Card style={{ marginBottom: 16 }} styles={{ body: { padding: '16px 24px' } }}>
        <Typography.Title level={4} style={{ margin: 0 }}>
          <ApiOutlined style={{ color: token.colorPrimary, marginRight: 8 }} />Power Factor Correction
          <Tag color="gold" style={{ marginLeft: 10, verticalAlign: 'middle' }}>Prototype</Tag>
        </Typography.Title>
        <Typography.Text type="secondary">Motor inventory, capacitor sizing and building roll-up</Typography.Text>
      </Card>

      <Alert type="info" showIcon closable style={{ marginBottom: 16 }}
        message="Prototype — methodology pending review"
        description="Load kW is held constant when PF is corrected. Energy savings count only reduced upstream line losses; utility PF / kVA demand savings appear only when a demand rate is entered (tariff basis still to be confirmed)." />

      {error && <Alert type="error" showIcon message={error} closable onClose={() => setError('')} style={{ marginBottom: 16 }} />}

      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={24} lg={9}>
          <Card title={<span><SettingOutlined /> Project assumptions</span>} loading={loading}
            extra={<Button type="primary" size="small" icon={<SaveOutlined />} disabled={!settingsDirty} onClick={handleSaveSettings}>Save</Button>}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
              {settingField('Target PF', 'targetPf', { min: 0.8, max: 1, step: 0.01 })}
              {settingField('Energy rate ($/kWh)', 'ratePerKwh', { min: 0, step: 0.01 })}
              {settingField('Upstream losses (% of load)', 'upstreamLossPct', { min: 0, max: 10, step: 0.5 })}
              {settingField('kVA demand ($/kVA-mo)', 'demandRatePerKva', { min: 0, step: 0.5 })}
              {settingField('VFD above (HP)', 'vfdThresholdHp', { min: 0, step: 1 })}
              <div>
                <div style={{ fontSize: 13, marginBottom: 6 }}>Frequency</div>
                <Segmented block value={settings.frequencyHz} options={[{ label: '60 Hz', value: 60 }, { label: '50 Hz', value: 50 }]}
                  onChange={v => updateSetting('frequencyHz', v as number)} />
              </div>
            </div>
          </Card>
        </Col>
        <Col xs={24} lg={15}>
          <Card title="Building roll-up" loading={loading}>
            {rollup.motorCount === 0 ? (
              <Empty description="Add motors to see the roll-up" />
            ) : (
              <Row gutter={[16, 16]} align="middle">
                <Col xs={24} md={15}>
                  <Row gutter={[16, 16]}>
                    <Col span={12}><Statistic title="Total load" value={f1(rollup.kw)} suffix="kW" /></Col>
                    <Col span={12}><Statistic title="Power factor" value={`${f2(rollup.pfBefore)} → ${f2(rollup.pfAfter)}`} /></Col>
                    <Col span={12}><Statistic title="kVA" value={`${f1(rollup.kvaBefore)} → ${f1(rollup.kvaAfter)}`} /></Col>
                    <Col span={12}>
                      <Statistic title={<Tooltip title="Sum of standard capacitor sizes (one per motor) vs a single bank sized from the aggregate to the target PF">Capacitors: per-motor / central</Tooltip>}
                        value={`${f1(rollup.individualKvar)} / ${f1(rollup.centralBankKvar)}`} suffix="kVAR" />
                    </Col>
                    <Col span={12}>
                      <Statistic title="Savings / yr" value={money(totalSavings)}
                        valueStyle={{ color: token.colorPrimary }} />
                      <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                        energy {money(rollup.energySavingsPerYear)} · demand {money(rollup.demandSavingsPerYear)}
                      </Typography.Text>
                    </Col>
                    <Col span={12}>
                      <Statistic title="Simple payback"
                        value={rollup.installedCost > 0 && totalSavings > 0 ? `${f1(rollup.installedCost / totalSavings)} yr` : '—'} />
                      {rollup.installedCost > 0 && <Typography.Text type="secondary" style={{ fontSize: 12 }}>on {money(rollup.installedCost)} installed</Typography.Text>}
                    </Col>
                  </Row>
                </Col>
                <Col xs={24} md={9} style={{ textAlign: 'center' }}>
                  <PowerTriangle kw={rollup.kw} kvarBefore={rollup.kvarBefore} kvarAfter={rollup.kvarAfter} />
                </Col>
              </Row>
            )}
          </Card>
        </Col>
      </Row>

      <WaveformExplorer motors={motors
        .filter(m => m.measuredPf > 0 && m.measuredPf <= 1)
        .map(m => ({ id: m.id, label: m.tag, pf: m.measuredPf }))} />

      <Card title={`Motor inventory (${motors.length})`} loading={loading}
        extra={
          <Space>
            <Button icon={<ExperimentOutlined />} onClick={() => openEditor(EXAMPLE_MOTOR)}>Example motor</Button>
            <Button type="primary" icon={<PlusOutlined />} onClick={() => openEditor(NEW_MOTOR)}>Add motor</Button>
          </Space>
        }>
        <Table<PFMotor>
          rowKey="id" size="middle" columns={columns} dataSource={motors} pagination={false}
          scroll={{ x: 'max-content' }}
          expandable={{ expandedRowRender: detail }}
          locale={{ emptyText: 'No motors yet — add one, or load the example motor from the walkthrough.' }}
        />
      </Card>

      <Modal
        title={editing?.id ? 'Edit motor' : 'Add motor'} open={!!editing} width={680}
        onCancel={() => setEditing(null)} onOk={handleSaveMotor} okText="Save motor"
        okButtonProps={{ loading: saving, icon: <SaveOutlined /> }} destroyOnHidden
      >
        <Form form={form} layout="vertical" requiredMark="optional">
          <Row gutter={12}>
            <Col xs={12} md={8}><Form.Item name="tag" label="Tag" rules={[{ required: true, message: 'Required' }]}><Input placeholder="e.g. P-2" /></Form.Item></Col>
            <Col xs={12} md={16}><Form.Item name="location" label="Location / serves"><Input placeholder="e.g. Boiler room — HW pump" /></Form.Item></Col>
            <Col xs={8}><Form.Item name="hp" label="Nameplate HP"><InputNumber style={{ width: '100%' }} min={0} /></Form.Item></Col>
            <Col xs={8}><Form.Item name="voltage" label="Voltage (L-L)" rules={[{ required: true, message: 'Required' }]}><InputNumber style={{ width: '100%' }} min={1} /></Form.Item></Col>
            <Col xs={8}>
              <Form.Item name="phase" label="Phase" rules={[{ required: true }]}>
                <Segmented block options={[{ label: '3φ', value: 3 }, { label: '1φ', value: 1 }]} />
              </Form.Item>
            </Col>
            <Col xs={8}><Form.Item name="amps" label="Measured amps" rules={[{ required: true, message: 'Required' }]}><InputNumber style={{ width: '100%' }} min={0} step={0.1} /></Form.Item></Col>
            <Col xs={8}>
              <Form.Item name="measuredPf" label="Measured PF" rules={[{ required: true, type: 'number', min: 0.01, max: 1, message: '0–1' }]}>
                <InputNumber style={{ width: '100%' }} min={0} max={1} step={0.01} />
              </Form.Item>
            </Col>
            <Col xs={8}><Form.Item name="targetPf" label="Target PF" tooltip="Leave blank to use the project target"><InputNumber style={{ width: '100%' }} min={0.8} max={1} step={0.01} placeholder={String(settings.targetPf)} /></Form.Item></Col>
            <Col xs={8}><Form.Item name="hoursPerDay" label="Hours / day" rules={[{ required: true }]}><InputNumber style={{ width: '100%' }} min={0} max={24} /></Form.Item></Col>
            <Col xs={8}><Form.Item name="daysPerYear" label="Days / year" rules={[{ required: true }]}><InputNumber style={{ width: '100%' }} min={0} max={366} /></Form.Item></Col>
            <Col xs={8}><Form.Item name="installedCost" label="Installed cost ($)"><InputNumber style={{ width: '100%' }} min={0} step={50} /></Form.Item></Col>
            <Col span={24}><Form.Item name="notes" label="Notes"><Input.TextArea rows={2} /></Form.Item></Col>
          </Row>
        </Form>
        {preview?.valid && (
          <Alert type={preview.warnings.length ? 'warning' : 'success'} showIcon
            message={`${f1(preview.kw)} kW · needs ${f2(preview.kvarNeeded)} kVAR → ${preview.capacitorKvar === null ? 'bank required' : `${preview.capacitorKvar} kVAR standard`} · PF ${f2(watched?.measuredPf ?? 0)} → ${f2(preview.pfAfter)} · ${f1(watched?.amps ?? 0)} → ${f1(preview.ampsAfter)} A`}
            description={preview.warnings.length ? preview.warnings.join(' ') : undefined} />
        )}
      </Modal>
    </div>
  );
};

export default PowerFactorModule;
